/**
 * SLD (Single Line Diagram) Object Classification
 *
 * Single source of truth for the electrical object types used when annotating
 * SLDs. Mirrors the `SldObjectHierarchy` tab of the "SLD Taxonomy Draft" sheet
 * (Hasma is the final owner). Any change must track that sheet.
 *
 * Deliberately a SIBLING of `pnidClassification.ts`, not an extension of it:
 * SLD annotation is a separate system that shares the editor canvas but not the
 * P&ID document-type family, the HITL overlay, or the emit/answering tier.
 * See the SLD annotation design doc.
 *
 * Level I   — top-level category (Electrical, Annotation)
 * Level II  — subcategory within Level I
 * Level III — specific object type within Level II
 * geometry  — how the object is drawn, straight from the sheet's GEOMETRY column
 */

export type SldLevelI = 'Electrical' | 'Annotation'

/** How an object is drawn on the canvas. From the sheet's GEOMETRY column. */
export type SldGeometry = 'box' | 'line' | 'polygon'

export interface SldClassificationEntry {
  levelI: SldLevelI
  levelII: string
  levelIII: string
  geometry: SldGeometry
  notes?: string
}

/** The electrical branch — the 104 electrical leaves of the sheet, verbatim. */
const ELECTRICAL_ENTRIES: SldClassificationEntry[] = [
  // ── Source ────────────────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Source',                   levelIII: 'Utility Incomer',                         geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Source',                   levelIII: 'Generator, Synchronous',                  geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Source',                   levelIII: 'Generator, Emergency',                    geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Source',                   levelIII: 'UPS',                                     geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Source',                   levelIII: 'Battery Bank',                            geometry: 'box' },
  // ── Power Transformer ─────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Power Transformer',        levelIII: 'Two Winding',                             geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Transformer',        levelIII: 'Three Winding',                           geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Transformer',        levelIII: 'Autotransformer',                         geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Transformer',        levelIII: 'Isolation Transformer',                   geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Transformer',        levelIII: 'Rectifier Transformer',                   geometry: 'box' },
  // ── Current Transformer ───────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Current Transformer',      levelIII: 'Current Transformer',                     geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Current Transformer',      levelIII: 'Core Balance CT',                         geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Current Transformer',      levelIII: 'Bushing CT',                              geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Current Transformer',      levelIII: 'Auxiliary CT',                            geometry: 'box' },
  // ── Potential Transformer ─────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Potential Transformer',    levelIII: 'Potential Transformer',                   geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Potential Transformer',    levelIII: 'Capacitive Voltage Transformer',          geometry: 'box' },
  // ── Grounding Transformer ─────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Grounding Transformer',    levelIII: 'Zig-Zag Grounding Transformer',           geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Grounding Transformer',    levelIII: 'Wye-Delta Grounding Transformer',         geometry: 'box' },
  // ── Switching ─────────────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: '52 Circuit Breaker, Drawout',             geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: '52 Circuit Breaker, Fixed',               geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: '52 Circuit Breaker, Molded Case',         geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: '89 Disconnecting Switch',                 geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: '89 Fused Disconnecting Switch',           geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: 'Load Break Switch',                       geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: 'Earthing Switch',                         geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: 'Contactor',                               geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: 'Automatic Transfer Switch',               geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: 'Manual Transfer Switch',                  geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: 'Static Switch',                           geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Switching',                levelIII: 'Maintenance Bypass Switch',               geometry: 'box' },
  // ── Protection ────────────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '2 Time Delay Starting Relay',             geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '21 Distance Relay',                       geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '25 Synchronism Check Relay',              geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '27 Undervoltage Relay',                   geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '32 Directional Power Relay',              geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '37 Undercurrent Relay',                   geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '40 Loss of Field Relay',                  geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '46 Current Balance Relay',                geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '47 Phase Sequence Voltage Relay',         geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '49 Thermal Relay',                        geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '50 Instantaneous Overcurrent Relay',      geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '50N Instantaneous Ground Overcurrent Relay', geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '51 AC Time Overcurrent Relay',            geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '51N AC Time Ground Overcurrent Relay',    geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '55 Power Factor Relay',                   geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '59 Overvoltage Relay',                    geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '60 Voltage Balance Relay',                geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '63 Pressure Relay',                       geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '64 Ground Detector Relay',                geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '67 AC Directional Overcurrent Relay',     geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '67N Directional Ground Overcurrent Relay', geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '81 Frequency Relay',                      geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '86 Lockout Relay',                        geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '87B Bus Differential Relay',              geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '87G Generator Differential Relay',        geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '87M Motor Differential Relay',            geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '87T Transformer Differential Relay',      geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: '94 Tripping Relay',                       geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: 'Multifunction Relay',                     geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: 'Fuse',                                    geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: 'Surge Arrester',                          geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: 'Lightning Arrester',                      geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Protection',               levelIII: 'Overload Relay',                          geometry: 'box' },
  // ── Bus ───────────────────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Bus',                      levelIII: 'Main Bus',                                geometry: 'line' },
  { levelI: 'Electrical', levelII: 'Bus',                      levelIII: 'Bus Section',                             geometry: 'line' },
  { levelI: 'Electrical', levelII: 'Bus',                      levelIII: 'Bus Tie',                                 geometry: 'line' },
  { levelI: 'Electrical', levelII: 'Bus',                      levelIII: 'Busway',                                  geometry: 'line' },
  // ── Distribution Equipment ────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Distribution Equipment',   levelIII: 'Switchgear Lineup',                       geometry: 'polygon' },
  { levelI: 'Electrical', levelII: 'Distribution Equipment',   levelIII: 'Motor Control Center',                    geometry: 'polygon' },
  { levelI: 'Electrical', levelII: 'Distribution Equipment',   levelIII: 'Panelboard',                              geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Distribution Equipment',   levelIII: 'Distribution Board',                      geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Distribution Equipment',   levelIII: 'Switchrack',                              geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Distribution Equipment',   levelIII: 'Terminal Box',                            geometry: 'box' },
  // ── Load ──────────────────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Load',                     levelIII: 'Induction Motor',                         geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Load',                     levelIII: 'Synchronous Motor',                       geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Load',                     levelIII: 'Heater',                                  geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Load',                     levelIII: 'Lighting Panel',                          geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Load',                     levelIII: 'Receptacle Panel',                        geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Load',                     levelIII: 'Static Load',                             geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Load',                     levelIII: 'Resistor',                                geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Load',                     levelIII: 'Motor',                                   geometry: 'box' },
  // ── Power Conditioning ────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Power Conditioning',       levelIII: 'Variable Frequency Drive',                geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Conditioning',       levelIII: 'Soft Starter',                            geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Conditioning',       levelIII: 'Capacitor Bank',                          geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Conditioning',       levelIII: 'Harmonic Filter',                         geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Conditioning',       levelIII: 'Reactor',                                 geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Conditioning',       levelIII: 'Rectifier',                               geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Conditioning',       levelIII: 'Inverter',                                geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Power Conditioning',       levelIII: 'Diode',                                   geometry: 'box' },
  // ── Grounding ─────────────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Grounding',                levelIII: 'Earth Connection',                        geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Grounding',                levelIII: 'Neutral Grounding Resistor',              geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Grounding',                levelIII: 'High Resistance Grounding',               geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Grounding',                levelIII: 'Ground Conductor',                        geometry: 'line' },
  // ── Metering ──────────────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Metering',                 levelIII: 'Ammeter',                                 geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Metering',                 levelIII: 'Voltmeter',                               geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Metering',                 levelIII: 'Wattmeter',                               geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Metering',                 levelIII: 'Multifunction Meter',                     geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Metering',                 levelIII: 'Power Monitor',                           geometry: 'box' },
  // ── Line ──────────────────────────────────────────────────────
  { levelI: 'Electrical', levelII: 'Line',                     levelIII: 'Feeder',                                  geometry: 'line' },
  { levelI: 'Electrical', levelII: 'Line',                     levelIII: 'Cable',                                   geometry: 'line' },
  { levelI: 'Electrical', levelII: 'Line',                     levelIII: 'Neutral Conductor',                       geometry: 'line' },
  { levelI: 'Electrical', levelII: 'Line',                     levelIII: 'Control Line',                            geometry: 'line' },
  { levelI: 'Electrical', levelII: 'Line',                     levelIII: 'Line Crossing',                           geometry: 'box' },
  { levelI: 'Electrical', levelII: 'Line',                     levelIII: 'Line Break',                              geometry: 'box' },
]

/** The annotation branch — text and markup on the drawing, not electrical objects. */
const ANNOTATION_ENTRIES: SldClassificationEntry[] = [
  // ── Text ──────────────────────────────────────────────────────
  { levelI: 'Annotation', levelII: 'Text',   levelIII: 'Tag',           geometry: 'box' },
  { levelI: 'Annotation', levelII: 'Text',   levelIII: 'Annotation',    geometry: 'box' },
  { levelI: 'Annotation', levelII: 'Text',   levelIII: 'Attribute',     geometry: 'box' },
  // ── Object ────────────────────────────────────────────────────
  { levelI: 'Annotation', levelII: 'Object', levelIII: 'Future Marker', geometry: 'box' },
  { levelI: 'Annotation', levelII: 'Object', levelIII: 'Cloud',         geometry: 'box' },
]

export const SLD_CLASSIFICATION: SldClassificationEntry[] = [
  ...ELECTRICAL_ENTRIES,
  ...ANNOTATION_ENTRIES,
]

// ── Lookup ────────────────────────────────────────────────────────────────────

/** The entry for an exact (Level I, Level II, Level III) triple, if it exists. */
export function findEntry(
  levelI: SldLevelI,
  levelII: string,
  levelIII: string,
): SldClassificationEntry | undefined {
  return SLD_CLASSIFICATION.find(
    (e) => e.levelI === levelI && e.levelII === levelII && e.levelIII === levelIII,
  )
}

/**
 * The sheet's DETECTION TAG for an entry: `LevelI_LevelII_LevelIII`.
 *
 * Derived rather than stored — the sheet carries it as a column, but it is
 * always exactly this join, so keeping it would be a second source of truth
 * that can disagree with the three levels beside it.
 */
export function detectionTag(entry: SldClassificationEntry): string {
  return `${entry.levelI}_${entry.levelII}_${entry.levelIII}`
}

// ── Node-type resolution ──────────────────────────────────────────────────────

/**
 * Maps a classification selection to the internal graph NodeType.
 * This is the single place that owns this coupling for SLDs.
 *
 *   Annotation ▸ Text ▸ Tag → 'tag'        (tag text: `52-101A`, `MCC-295`)
 *   geometry === 'line'    → 'connection' (buses, feeders, cables, ground conductors)
 *   everything else        → 'symbol'
 *
 * The Tag triple matches P&ID's, so a tag node means the same thing in both.
 *
 * Connection detection reads the sheet's own GEOMETRY column rather than
 * hard-coding Level II names, so a new `line`-geometry leaf needs no code
 * change. (The P&ID equivalent hard-codes `Piping ▸ Line` and
 * `Instrumentation ▸ Line`; that sheet has no geometry column to key off.)
 *
 * An unknown triple resolves to 'symbol' — the safe default, matching P&ID.
 */
export function resolveNodeType(
  levelI: SldLevelI,
  levelII: string,
  levelIII?: string | null,
): 'symbol' | 'tag' | 'connection' {
  if (levelI === ANNOTATION_LEVEL_I && levelII === 'Text' && levelIII === 'Tag') return 'tag'
  const entry = levelIII ? findEntry(levelI, levelII, levelIII) : undefined
  if (entry?.geometry === 'line') return 'connection'
  return 'symbol'
}

// ── Derived helpers ───────────────────────────────────────────────────────────

/** All distinct Level I categories, in display order. */
export const SLD_LEVEL_I_VALUES: SldLevelI[] = ['Electrical', 'Annotation']

/** The Level I holding text and markup — the only home for a tag node. */
export const ANNOTATION_LEVEL_I: SldLevelI = 'Annotation'

/**
 * Level-I categories a node of the given type may be classified as.
 *
 * A tag/text node is always an Annotation element, so it is confined to that
 * branch, as on P&ID. Symbols and connections get every Level I.
 */
export function categoryOptionsForNodeType(nodeType: string): SldLevelI[] {
  // Return a fresh array — never hand out the module-level constant.
  return nodeType === 'tag' || nodeType === 'table_cell'
    ? [ANNOTATION_LEVEL_I]
    : [...SLD_LEVEL_I_VALUES]
}

/** All distinct Level II values for a given Level I, in sheet order. */
export function getLevelII(levelI: SldLevelI): string[] {
  const seen = new Set<string>()
  return SLD_CLASSIFICATION.filter((e) => e.levelI === levelI)
    .map((e) => e.levelII)
    .filter((v) => {
      if (seen.has(v)) return false
      seen.add(v)
      return true
    })
}

/** All distinct Level III values for a given Level I + Level II, in sheet order. */
export function getLevelIII(levelI: SldLevelI, levelII: string): string[] {
  return SLD_CLASSIFICATION.filter((e) => e.levelI === levelI && e.levelII === levelII).map(
    (e) => e.levelIII,
  )
}

/**
 * True when an object is missing a *required* classification field for approval.
 *
 * Category, Subcategory and Type are ALL required, Annotation included. The
 * P&ID gate exempts its Annotation branch, which needs only a Category; here
 * every Annotation leaf sits under a pickable (Level II, Level III) pair, so
 * there is no reason to accept less.
 *
 * The no-leaf escape is kept for safety: a (Category, Subcategory) offering no
 * Level III would otherwise be an unsatisfiable block. No such pair exists in
 * the sheet today, so this is a guard against a future edit, not a live case.
 */
export function isMissingRequiredType(
  category: string,
  subcategory: string,
  type: string,
): boolean {
  const cat = category.trim()
  const sub = subcategory.trim()
  if (type.trim()) return false
  if (cat && sub && getLevelIII(cat as SldLevelI, sub).length === 0) return false
  return true
}

/** Look up the notes for a specific entry. */
export function getEntryNotes(
  levelI: SldLevelI,
  levelII: string,
  levelIII: string,
): string | undefined {
  return findEntry(levelI, levelII, levelIII)?.notes
}
